import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Container, Grid, Stack, Typography, Button,
} from '@mui/material';
import ComingSoonModal from '../ComingSoonModal';
import AndroidIcon from '@mui/icons-material/Android';
import AppleIcon from '@mui/icons-material/Apple';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EventIcon from '@mui/icons-material/Event';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import DescriptionIcon from '@mui/icons-material/Description';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PostAddIcon from '@mui/icons-material/PostAdd';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ScreenshotCarousel from './ScreenshotCarousel';
import { FONT_PRIMARY, FONT_WEIGHT } from '../../theme/fonts';
import { getCarouselSlides } from '../../utils/carouselAssets';

// Using CSS variables from index.css for consistency with mobile app
const brand = { 
  primary: 'var(--primary-color, #047857)',  // emerald-700
  primaryHover: 'var(--primary-color-hover, #065f46)',  // emerald-800
  accent: 'var(--secondary-color, #0e918c)',  // teal
};

// Optional per-slide captions (keyed by filename number). Add titles as you go.
const CAROUSEL_TITLES = {
  1: 'Choose your civic experience',
  2: 'Find your representatives',
  3: 'Connect with your representatives',
  4: 'Personalized civic action at your fingertips',
  5: 'Explore federal policy',
  6: 'Stay ready for every election',
  7: 'Submit public comments',
  8: 'Browse every legislature',
  9: 'Powerful policy filtering',
  10: 'Track policy from start to finish',
  11: 'Understand policy in plain language',
  12: 'Follow the money behind campaigns',
  13: 'A civic feed built for action',
  14: 'Organize volunteers and follow-up actions',
};

// Display order — dashboard first, then remaining slides by filename number.
const CAROUSEL_ORDER = [4, 1, 2, 3, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];

const APP_SCREENSHOTS = getCarouselSlides(CAROUSEL_ORDER, CAROUSEL_TITLES);

export default function HomeSectionOne() {
  const [modalOpen, setModalOpen] = useState(false);

  const appFeatures = [
    { icon: <DashboardIcon />, title: 'Your personalized civic dashboard' },
    { icon: <EventIcon />, title: 'Events with follow-up actions' },
    {
      icon: <VerifiedUserIcon />,
      title: 'Verified accounts',
      verificationSectionLink: '/features#verification',
    },
    { icon: <QrCodeScannerIcon />, title: 'QR check-ins for event attendance' },
    { icon: <DescriptionIcon />, title: 'Engage in policy that matters to you' },
    { icon: <PostAddIcon />, title: 'A civic feed built for action' },
    { icon: <EmojiEventsIcon />, title: 'Earn recognition for civic participation' },
    { icon: <AccountCircleIcon />, title: 'Choose the account type that fits you' },
  ];

  return (
    <Box
      position="relative"
      sx={{
        overflow: 'hidden',
        py: { xs: 6, md: 10 },
        fontFamily: FONT_PRIMARY,
        // Subtle moving gradient — same near-white palette as before, just
        // gently animated so the background feels alive without the color
        // itself appearing to change.
        background: 'linear-gradient(120deg, #f8fafc 0%, #f6f9f8 30%, #f3f8f6 50%, #f6f9f8 70%, #f8fafc 100%)',
        backgroundSize: '300% 300%',
        animation: 'ctHeroBgShift 22s ease infinite',
        '@keyframes ctHeroBgShift': {
          '0%': { backgroundPosition: '0% 50%' },
          '50%': { backgroundPosition: '100% 50%' },
          '100%': { backgroundPosition: '0% 50%' },
        },
      }}
    >
      {/* decorative blobs */}
      <Box
        sx={{
          pointerEvents: 'none',
          position: 'absolute',
          top: { xs: -60, md: -100 },
          left: { xs: -60, md: -120 },
          width: { xs: 240, md: 360 },
          height: { xs: 240, md: 360 },
          borderRadius: '50%',
          filter: 'blur(40px)',
          background: 'radial-gradient(closest-side, var(--primary-color, #047857) 0%, rgba(4,120,87,0) 70%)',
          opacity: 0.15,
        }}
      />
      <Box
        sx={{
          pointerEvents: 'none',
          position: 'absolute',
          top: { xs: 220, md: 280 },
          right: { xs: -60, md: -120 },
          width: { xs: 320, md: 480 },
          height: { xs: 320, md: 480 },
          borderRadius: '50%',
          filter: 'blur(40px)',
          background: 'radial-gradient(closest-side, #a7f3d0 0%, rgba(167,243,208,0) 70%)',  /* emerald-200 */
          opacity: 0.25,
        }}
      />

      <Container 
        maxWidth={false}
        sx={{
          maxWidth: '1400px',
          mx: 'auto',
          px: { xs: 'clamp(1rem, 4vw, 3rem)', md: 'clamp(1rem, 4vw, 3rem)' },
        }}
      >
        <Grid container spacing={8} alignItems="center">
          {/* left column */}
          <Grid item xs={12} md={7}>
            <Stack spacing={3}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: FONT_WEIGHT.semibold,
                  lineHeight: 1.1,
                  fontSize: { xs: 36, md: 52 },
                }}
              >
                We're putting the power in your pocket. 
                {/* <Box component="span" sx={{ color: brand.accent }}>
                  in your pocket.
                </Box> */}
              </Typography>

              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' }, 
                  flexWrap: 'wrap',
                  gap: 2, 
                  mt: 1 
                }}
              >
                <Button
                  variant="contained"
                  component="a"
                  href="https://apps.apple.com/us/app/civixthread/id6756985264"
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<AppleIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #047857 0%, #059669 50%, #047857 100%)',
                    color: '#fff',
                    borderRadius: 3,
                    px: 4,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: FONT_WEIGHT.semibold,
                    fontSize: '1rem',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #065f46 0%, #047857 50%, #065f46 100%)',
                    },
                  }}
                >
                  Download for iOS
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AndroidIcon />}
                  onClick={() => setModalOpen(true)}
                  sx={{
                    background: 'linear-gradient(135deg, #047857 0%, #059669 50%, #047857 100%)',
                    color: '#fff',
                    borderRadius: 3,
                    px: 4,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: FONT_WEIGHT.semibold,
                    fontSize: '1rem',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #065f46 0%, #047857 50%, #065f46 100%)',
                    },
                  }}
                >
                  Android — Coming soon
                </Button>
              </Box>

              {/* Secondary path for people who don't want the app yet */}
              <Button
                component={RouterLink}
                to="/auth/signup"
                variant="text"
                endIcon={<ChevronRightIcon sx={{ fontSize: 24 }} />}
                sx={{
                  alignSelf: 'flex-start',
                  color: brand.primary,
                  textTransform: 'none',
                  fontWeight: FONT_WEIGHT.bold,
                  fontSize: '1.05rem',
                  borderRadius: 3,
                  px: 1,
                  py: 0.5,
                  minHeight: 44,
                  '&:hover': {
                    bgcolor: 'rgba(4, 120, 87, 0.06)',
                  },
                  '&:focus-visible': {
                    outline: 'none',
                    boxShadow: `0 0 0 2px ${brand.primary}`,
                  },
                }}
              >
                Or continue on the web
              </Button>

              {/* App Features Grid */}
              <Grid container spacing={2} sx={{ mt: 2 }}>
                {appFeatures.map((feature) => (
                  <Grid item xs={6} key={feature.title}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: 2,
                          bgcolor: `${brand.primary}15`,
                          color: brand.primary,
                          display: 'flex',
                        }}
                      >
                        {feature.icon}
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" alignItems="center" flexWrap="nowrap" spacing={0.5}>
                          <Typography
                            variant="subtitle2"
                            component="div"
                            sx={{
                              minWidth: 0,
                              flex: '1 1 auto',
                              fontSize: { xs: '0.9rem', md: '0.95rem' },
                              fontWeight: FONT_WEIGHT.semibold,
                              lineHeight: 1.35,
                            }}
                          >
                            {feature.verificationSectionLink ? (
                              <>
                                Verified accounts
                                <Box
                                  component="a"
                                  href={feature.verificationSectionLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-label="Open how account verification works in a new tab"
                                  title="Open in new tab"
                                  sx={{
                                    display: 'inline-flex',
                                    verticalAlign: 'middle',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 28,
                                    height: 28,
                                    ml: 0.15,
                                    mr: -0.1,
                                    borderRadius: 1,
                                    color: brand.primary,
                                    textDecoration: 'none',
                                    '&:hover': {
                                      bgcolor: 'rgba(4, 120, 87, 0.08)',
                                    },
                                    '&:focus-visible': {
                                      outline: '2px solid',
                                      outlineColor: brand.primary,
                                      outlineOffset: 1,
                                    },
                                  }}
                                >
                                  <OpenInNewIcon sx={{ fontSize: 16 }} />
                                </Box>
                              </>
                            ) : (
                              feature.title
                            )}
                          </Typography>
                        </Stack>
                      </Box>
                    </Stack>
                  </Grid>
                ))}
              </Grid>

              <Button
                component={RouterLink}
                to="/features"
                variant="text"
                endIcon={<ChevronRightIcon sx={{ fontSize: 24 }} />}
                sx={{
                  alignSelf: 'flex-start',
                  mt: 2,
                  color: brand.primary,
                  textTransform: 'none',
                  fontWeight: FONT_WEIGHT.bold,
                  fontSize: '1.05rem',
                  borderRadius: 3,
                  px: 1,
                  py: 0.5,
                  minHeight: 44,
                  '&:hover': {
                    bgcolor: 'rgba(4, 120, 87, 0.06)',
                  },
                  '&:focus-visible': {
                    outline: 'none',
                    boxShadow: `0 0 0 2px ${brand.primary}`,
                  },
                }}
              >
                Explore the rest
              </Button>
            </Stack>
          </Grid>

          {/* right column — real app screenshot carousel */}
          <Grid item xs={12} md={5} sx={{ overflow: 'visible' }}>
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                py: { xs: 1, md: 0 },
              }}
            >
              {/* Accent parallelogram centered behind the carousel — tighter
                  corners + a bolder skew so it reads as deliberate geometry
                  rather than a soft shadow, with a lighter gradient that's
                  actually visible as it animates. */}
              <Box
                sx={{
                  pointerEvents: 'none',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: { xs: 205, sm: 270, md: 250, lg: 320 },
                  height: { xs: 305, sm: 423, md: 375, lg: 513 },
                  borderRadius: '12px',
                  opacity: 0.5,
                  background: 'linear-gradient(135deg, #047857 0%, #059669 35%, #10b981 65%, #047857 100%)',
                  backgroundSize: '300% 300%',
                  animation: 'ct-accent-shift 9s ease infinite',
                  '@keyframes ct-accent-shift': {
                    '0%': { backgroundPosition: '0% 50%' },
                    '50%': { backgroundPosition: '100% 50%' },
                    '100%': { backgroundPosition: '0% 50%' },
                  },
                  transform: 'translate(-50%, calc(-50% + 12px)) skewX(-13deg)',
                  zIndex: 0,
                }}
              />
              <Box sx={{ position: 'relative', zIndex: 1, width: '100%', overflow: 'visible' }}>
                <ScreenshotCarousel slides={APP_SCREENSHOTS} />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
      
      <ComingSoonModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </Box>
  );
}
